import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";

import "../../assets/styles/chart.css";
import { useViewBarGraphQuery } from "../../hooks/dashboard/graph.bar.view";
import { mapShareAnalyticsGraph } from "../../utils/mapShareAnalyticsGraph";

const AnalyticsBarChart = ({
  data: propData,
  bars: propBars = [],
  xKey: propXKey = "day",
  title: propTitle = "Share Analytics",
  subtitle: propSubtitle = "Weekly share distribution",
  height: propHeight = 300,
  showGrid: propShowGrid = true,
  showLegend: propShowLegend = false,
}) => {
  const fetchFromApi = propData == null;

  const {
    data: fetchedData,
    isLoading,
    isError,
  } = useViewBarGraphQuery({
    enabled: fetchFromApi,
  });

  const chartConfig = useMemo(() => {
    if (fetchFromApi) {
      return (
        mapShareAnalyticsGraph(fetchedData, {
          bars: propBars,
          xKey: propXKey,
          title: propTitle,
          subtitle: propSubtitle,
          height: propHeight,
          showGrid: propShowGrid,
          showLegend: propShowLegend,
        }) ?? {
          data: [],
          bars: [],
          xKey: propXKey,
          title: propTitle,
          subtitle: propSubtitle,
          height: propHeight,
          showGrid: propShowGrid,
          showLegend: propShowLegend,
        }
      );
    }

    return {
      data: propData ?? [],
      bars: propBars.length
        ? propBars
        : [{ dataKey: "shareCount", color: "#5c308d", name: "Shares" }],
      xKey: propXKey,
      title: propTitle,
      subtitle: propSubtitle,
      height: propHeight,
      showGrid: propShowGrid,
      showLegend: propShowLegend,
    };
  }, [
    fetchFromApi,
    fetchedData,
    propData,
    propBars,
    propXKey,
    propTitle,
    propSubtitle,
    propHeight,
    propShowGrid,
    propShowLegend,
  ]);

  const { data, bars, xKey, title, subtitle, height, showGrid, showLegend } =
    chartConfig;

  const minChartWidth = Math.max(data?.length * 80, 600);

  if (fetchFromApi && isLoading) {
    return <div className="chart-card chart-loading">Loading chart...</div>;
  }

  if (fetchFromApi && isError) {
    return (
      <div className="chart-card chart-error">Could not load chart data.</div>
    );
  }

  if (!data?.length || !bars?.length) {
    return (
      <div className="chart-card chart-empty">
        {title && (
          <div className="chart-header">
            <h3 className="chart-title">{title}</h3>
            {subtitle && <p className="chart-subtitle">{subtitle}</p>}
          </div>
        )}
        <p
          style={{
            textAlign: "center",
            color: "#6b7280",
            padding: "40px 20px",
          }}
        >
          No data available
        </p>
      </div>
    );
  }

  return (
    <div className="chart-card">
      {title && (
        <div className="chart-header">
          <h3 className="chart-title">{title}</h3>
          {subtitle && <p className="chart-subtitle">{subtitle}</p>}
        </div>
      )}
      <div className="chart-wrapper">
        <div style={{ minWidth: minChartWidth }}>
          <ResponsiveContainer width="100%" height={height}>
            <BarChart
              data={data}
              barCategoryGap="30%"
              maxBarSize={56}
              margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
            >
              {showGrid && (
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#e5e7eb"
                />
              )}

              <XAxis
                dataKey={xKey}
                type="category"
                stroke="#6b7280"
                tick={{ fontSize: 13, fill: "#6b7280" }}
                axisLine={{ stroke: "#e5e7eb" }}
                tickLine={false}
              />
              <YAxis
                type="number"
                stroke="#6b7280"
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
                domain={[0, "auto"]}
                allowDecimals={false}
              />

              <Tooltip
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
                cursor={{ fill: "rgba(92, 48, 141, 0.08)" }}
              />
              {showLegend && <Legend />}

              {bars.map((bar) => (
                <Bar
                  key={bar.dataKey}
                  dataKey={bar.dataKey}
                  fill={bar.color || "#5c308d"}
                  name={bar.name}
                  radius={[8, 8, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsBarChart;
